import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import userAPI from "../../api/UserApi";
import { setAccessToken } from "../../api/axiosInstance";
import { handleApiValidationError } from "../../utils/apiErrorHandler";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";

// ─── SVG Icons ──────────────────────────────────────────────────────────────
const IconMail = () => (
  <svg className="login-input-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M2.5 5.5l7.5 5.5 7.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="1" y="4" width="18" height="12" rx="2" strokeLinejoin="round" />
  </svg>
);

const IconLock = () => (
  <svg className="login-input-icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="4" y="9" width="12" height="9" rx="2" />
    <path d="M7 9V6a3 3 0 0 1 6 0v3" strokeLinecap="round" />
    <circle cx="10" cy="13.5" r="1" fill="currentColor" />
  </svg>
);

const IconEye = ({ show }) =>
  show ? (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M1 10s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7z" />
      <circle cx="10" cy="10" r="3" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 3l14 14M8.5 8.6A3 3 0 0 0 10 13a3 3 0 0 0 3-3 3 3 0 0 0-.6-1.8" />
      <path d="M6.1 6.2C3.9 7.5 2 10 2 10s3 6 8 6a8.2 8.2 0 0 0 3.9-1M10 4c5 0 8 6 8 6s-.8 1.6-2.2 3" />
    </svg>
  );

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// Decorative SVG watermark
const MandalaWatermark = () => (
  <svg className="login-visual-watermark" viewBox="0 0 400 400" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="200" cy="200" r="190" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="150" stroke="white" strokeWidth="0.5" />
    <circle cx="200" cy="200" r="110" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="70" stroke="white" strokeWidth="0.5" />
    <circle cx="200" cy="200" r="30" stroke="white" strokeWidth="1" />
    {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      const x1 = 200 + 30 * Math.cos(rad);
      const y1 = 200 + 30 * Math.sin(rad);
      const x2 = 200 + 190 * Math.cos(rad);
      const y2 = 200 + 190 * Math.sin(rad);
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="white" strokeWidth="0.5" />;
    })}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      const cx = 200 + 150 * Math.cos(rad);
      const cy = 200 + 150 * Math.sin(rad);
      return <circle key={i} cx={cx} cy={cy} r="8" stroke="white" strokeWidth="0.8" />;
    })}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => {
      const rad = (angle * Math.PI) / 180;
      const cx = 200 + 110 * Math.cos(rad);
      const cy = 200 + 110 * Math.sin(rad);
      return <circle key={i} cx={cx} cy={cy} r="5" stroke="white" strokeWidth="0.6" />;
    })}
    <polygon points="200,10 210,190 200,200 190,190" stroke="white" strokeWidth="0.5" fill="none" />
    <polygon points="200,390 210,210 200,200 190,210" stroke="white" strokeWidth="0.5" fill="none" />
    <polygon points="10,200 190,190 200,200 190,210" stroke="white" strokeWidth="0.5" fill="none" />
    <polygon points="390,200 210,190 200,200 210,210" stroke="white" strokeWidth="0.5" fill="none" />
  </svg>
);

const CornerOrnament = () => (
  <svg className="login-corner-ornament" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2 L62 2 L62 62" stroke="white" strokeWidth="1.5" fill="none" />
    <path d="M2 2 L2 62 L62 62" stroke="white" strokeWidth="0.5" fill="none" strokeDasharray="4 4" />
    <circle cx="32" cy="32" r="8" stroke="white" strokeWidth="1" />
    <line x1="32" y1="2" x2="32" y2="24" stroke="white" strokeWidth="0.8" />
    <line x1="32" y1="40" x2="32" y2="62" stroke="white" strokeWidth="0.8" />
    <line x1="2" y1="32" x2="24" y2="32" stroke="white" strokeWidth="0.8" />
    <line x1="40" y1="32" x2="62" y2="32" stroke="white" strokeWidth="0.8" />
  </svg>
);

// ─── Main Component ──────────────────────────────────────────────────────────
export default function LoginCanvas() {
  useAlphabeticInput();
  const navigate = useNavigate();

  // ── Form State (LoginCanvas pattern)
  const INITIAL_FORM_DATA = { email: "", password: "" };
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors]     = useState({});
  const [loading, setLoading]   = useState(false);

  // ── UI State
  const [rememberMe,    setRememberMe]    = useState(false);
  const [showPassword,  setShowPassword]  = useState(false);

  // ── Validation (LoginCanvas logic)
  const emailRegex     = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValidEmail   = emailRegex.test(formData.email);
  const isValidPassword = formData.password.length >= 6;

  const validate = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "Please Enter Email Address";
    } else if (!isValidEmail) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.password) {
      newErrors.password = "Enter Password";
    } else if (!isValidPassword) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on type
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ── Submit (LoginCanvas API logic)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await userAPI.login(formData);

      if (res.code === 200) {
        localStorage.setItem(
          "token",
          JSON.stringify({
            email:    res.data.email,
            username: res.data.username,
          }),
        );
        localStorage.setItem("accessToken", res.data.accessToken);
        setAccessToken(res.data.accessToken);
        navigate("/users/dashboard");
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setLoading(false);
    }
  };

  // ── GSAP Refs
  const visualPanelRef   = useRef(null);
  const visualContentRef = useRef(null);
  const cardRef          = useRef(null);
  const formGroupRefs    = useRef([]);
  const optionsRef       = useRef(null);
  const btnPrimaryRef    = useRef(null);
  const dividerRef       = useRef(null);
  const socialRef        = useRef(null);
  const registerRef      = useRef(null);

  const addFormGroupRef = (el) => {
    if (el && !formGroupRefs.current.includes(el)) {
      formGroupRefs.current.push(el);
    }
  };

  // ── GSAP entrance animation
  useEffect(() => {
    if (typeof gsap === "undefined") return;

    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(
      visualPanelRef.current,
      { clipPath: "inset(0 100% 0 0)" },
      { clipPath: "inset(0 0% 0 0)", duration: 1.1 },
      0
    );
    tl.fromTo(
      visualContentRef.current,
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.9 },
      0.45
    );
    tl.to(cardRef.current,          { opacity: 1, y: 0, duration: 0.85 },              0.2  );
    tl.to(formGroupRefs.current,    { opacity: 1, y: 0, duration: 0.55, stagger: 0.1 }, 0.55 );
    tl.to(optionsRef.current,       { opacity: 1, y: 0, duration: 0.45 },              0.85 );
    tl.to(btnPrimaryRef.current,    { opacity: 1, y: 0, duration: 0.45 },              0.95 );
    tl.to(dividerRef.current,       { opacity: 1, duration: 0.4 },                     1.05 );
    tl.to(socialRef.current,        { opacity: 1, y: 0, duration: 0.45 },              1.1  );
    tl.to(registerRef.current,      { opacity: 1, duration: 0.4 },                     1.2  );
  }, []);

  return (
    <>
      <div className="login-page-wrapper">

        {/* ── LEFT: Editorial Visual Panel ── */}
        <div className="login-visual-panel" ref={visualPanelRef}>
          <div className="login-visual-bg" />
          <MandalaWatermark />
          <div className="login-visual-overlay" />
          <CornerOrnament />

          <div className="login-visual-content" ref={visualContentRef}>
            <div className="login-brand-logo">BEGUM'S</div>
            <div className="login-tagline-eyebrow">Heritage · Craft · Legacy</div>
            <h2 className="login-tagline-headline">
              Timeless<br />
              <em>Elegance</em>
            </h2>
            <div className="login-visual-rule" />
            <p className="login-tagline-sub">
              Curated ethnic fashion rooted in centuries of artisanal craft —
              where every thread tells a story.
            </p>
          </div>
        </div>

        {/* ── RIGHT: Login Form Panel ── */}
        <div className="login-form-panel">
          <div className="login-card" ref={cardRef}>

            {/* Eyebrow */}
            <div className="login-card-eyebrow">
              <div className="login-card-eyebrow-line" />
              <span className="login-card-eyebrow-text">Welcome Back</span>
              <div className="login-card-eyebrow-line" />
            </div>

            {/* Title */}
            <h1 className="login-title">Sign In</h1>
            <p className="login-subtitle">Access your curated wardrobe</p>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate>

              {/* Email */}
              <div className="login-form-group" ref={addFormGroupRef}>
                <label htmlFor="login-email" className="login-label">
                  Email Address
                </label>
                <div className="login-input-wrapper">
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    className={`login-input${errors.email ? " is-error" : ""}`}
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    required
                  />
                  <IconMail />
                </div>
                {errors.email && (
                  <span className="login-field-error">{errors.email}</span>
                )}
              </div>

              {/* Password */}
              <div className="login-form-group" ref={addFormGroupRef}>
                <label htmlFor="login-password" className="login-label">
                  Password
                </label>
                <div className="login-input-wrapper">
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className={`login-input${errors.password ? " is-error" : ""}`}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    required
                  />
                  <IconLock />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <IconEye show={showPassword} />
                  </button>
                </div>
                {errors.password && (
                  <span className="login-field-error">{errors.password}</span>
                )}
              </div>

              {/* Options Row */}
              <div className="login-options-row" ref={optionsRef}>
                <label className="login-remember">
                  <input
                    type="checkbox"
                    className="login-checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span className="login-remember-text">Remember me</span>
                </label>
                <Link to="/forgot-password" className="login-forgot-link">
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="login-btn-primary"
                ref={btnPrimaryRef}
                disabled={loading}
              >
                {loading ? "Signing In…" : "Sign In"}
              </button>

            </form>

            {/* Divider */}
            {/* <div className="login-divider" ref={dividerRef}>
              <div className="login-divider-line" />
              <span className="login-divider-text">or continue with</span>
              <div className="login-divider-line" />
            </div> */}

            {/* Social Buttons */}
            {/* <div className="login-social-row" ref={socialRef}>
              <button type="button" className="login-btn-social">
                <GoogleIcon />
                Google
              </button>
              <button type="button" className="login-btn-social">
                <FacebookIcon />
                Facebook
              </button>
            </div> */}

            {/* Register Redirect */}
            <div className="login-register-row" ref={registerRef}>
              <span className="login-register-text">
                Don't have an account?
                <Link to="/users/register" className="login-register-link">
                  Register
                </Link>
              </span>
            </div>

          </div>
        </div>

      </div>
    </>
  );
}